import { useEffect, useState } from "preact/hooks";
import {
  buyHint,
  canBuyHint,
  guesses,
  hardModeSolve,
  HINT_COST,
  hintRevealed,
  puzzle,
  SPACES_COST,
  spacesPurchased,
  status,
} from "../game/state";

const FADE_MS = 300;

/** The "Reveal Hint" button. Mirrors `<Spaces />`: returns null in
    states where the button shouldn't render (no puzzle, post-purchase,
    game-over, or once the affordability fade-out has finished). The
    parent (`<TopBar />`) reserves the row's height. */
export function Hint() {
  const p = puzzle.value;
  const playing = status.value === "playing";
  const revealed = hintRevealed.value;
  const canBuy = canBuyHint.value;

  // Hooks must run unconditionally — declare them BEFORE any early
  // return. `useState`'s lazy initializer reads canBuy, which is fine
  // when the puzzle is null too (canBuyHint computes on a "playing"
  // gate that's always false in the loading state, so the initial
  // value is `false`).
  const [buttonMounted, setButtonMounted] = useState(canBuy);
  const fading = buttonMounted && !canBuy;

  useEffect(() => {
    if (canBuy && !buttonMounted) {
      setButtonMounted(true);
      return;
    }
    if (!canBuy && buttonMounted) {
      const t = setTimeout(() => setButtonMounted(false), FADE_MS);
      return () => clearTimeout(t);
    }
    return;
  }, [canBuy, buttonMounted]);

  if (p == null) return null;
  if (!playing) return null;
  if (revealed) return null;
  if (!buttonMounted) return null;

  return (
    <button
      type="button"
      class={`hint__button${fading ? " hint__button--fade" : ""}`}
      onClick={() => buyHint()}
      disabled={!canBuy}
      aria-label="Reveal hint (costs 2 guesses)"
    >
      Reveal Hint <span class="hint__cost">(costs 2 guesses)</span>
    </button>
  );
}

/** The revealed hint text, or an invisible placeholder reserving the
    row's vertical space during play before the buy. After game-over,
    `<GameOverPanel />` takes over the whole top bar — this component
    returns null. */
export function HintDisplay() {
  const p = puzzle.value;
  const playing = status.value === "playing";
  const revealed = hintRevealed.value;
  if (p == null) return null;
  if (!playing) return null;
  if (!revealed) return null;
  return (
    <p class="hint__text" aria-live="polite">
      <span class="hint__label">Hint:</span> {p.hint}
    </p>
  );
}

/** End-of-game panel: title, answer, byline, stats. Replaces the
    play-mode 3-row layout (buttons / hint / status) when the game ends. */
export function GameOverPanel() {
  const p = puzzle.value;
  if (p == null) return null;
  const playing = status.value === "playing";
  if (playing) return null;
  const won = status.value === "won";
  const hardMode = hardModeSolve.value;
  const used =
    guesses.value.length +
    (hintRevealed.value ? HINT_COST : 0) +
    (spacesPurchased.value ? SPACES_COST : 0);
  const total = p.maxGuesses;
  const title = hardMode
    ? "Solved in Hard Mode!"
    : won
      ? "Solved!"
      : "Out of guesses";
  return (
    <div class="hint hint--end">
      <p class="hint__title">{title}</p>
      <p class="hint__answer" aria-live="polite">
        {p.text}
      </p>
      <p class="hint__byline">— {p.byline}</p>
      <p class="hint__stats">
        {won ? `Solved in ${used} of ${total}` : `Used ${used} of ${total}`}
        {hintRevealed.value ? " · hint used" : ""}
        {spacesPurchased.value ? " · spaces used" : ""}
      </p>
    </div>
  );
}
